import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, ArrowLeft, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import Footer from '../components/Footer';
import TiccAIlogo from '../assets/TiccAIlogo.png';
import AuthModal from '../components/AuthModal';

const Contact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    subject: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState<false | 'signin' | 'signup'>(false);

  const openAuthModal = (mode: 'signin' | 'signup') => setIsAuthModalOpen(mode);
  const closeAuthModal = () => setIsAuthModalOpen(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus('idle');
    setErrorMessage('');

    try {
      // Store in Supabase
      const { error: supabaseError } = await supabase
        .from('contact_messages')
        .insert([formData]);

      if (supabaseError) throw supabaseError;

      // Send email via FormSubmit
      const formSubmitResponse = await fetch('https://formsubmit.co/ohm7p1234@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          _subject: formData.subject,
        })
      });

      if (!formSubmitResponse.ok) {
        throw new Error('Failed to send email');
      }

      setStatus('success');
      setFormData({ email: '', subject: '', message: '' });
    } catch (err) {
      setErrorMessage('Something went wrong. Please try again.');
      setStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-[#09101c] flex flex-col relative overflow-x-hidden">
      {/* Animated radial gradients and grid overlay for depth, matching HomePage/Pricing */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] pointer-events-none z-0">
        <div
          className="absolute inset-0 opacity-[0.5]"
          style={{
            background: `
              radial-gradient(circle at 45% 0%, rgba(139,  92, 246, 0.30)  0%, rgba(101,  62, 200, 0.20) 20%, rgba( 50,  35, 120, 0.12) 40%, rgba(  9,  16,  28, 0.06) 60%, transparent 80%)
            `,
            filter: 'blur(0px)',
            transform: 'translateZ(0)',
            willChange: 'transform'
          }}
        />
      </div>
      <ParticlesBackground />
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-20 -left-80 w-[600px] h-[600px] rounded-full animate-pulse" style={{background: 'radial-gradient(circle, rgba(255, 0, 128, 0.1), transparent)', filter: 'blur(100px)', transform: 'translateZ(0)', willChange: 'transform'}} />
        <div className="absolute top-[20%] right-[10%] w-[400px] h-[400px] rounded-full animate-pulse" style={{background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08), transparent)', filter: 'blur(80px)', animationDuration: '8s'}} />
        <div className="absolute top-[60%] left-[5%] w-[500px] h-[500px] rounded-full animate-pulse" style={{background: 'radial-gradient(circle, rgba(139, 92, 246, 0.08), transparent)', filter: 'blur(90px)', animationDuration: '10s'}} />
        <div className="absolute top-[40%] right-[15%] w-[300px] h-[300px] rounded-full animate-pulse" style={{background: 'radial-gradient(circle, rgba(67, 56, 202, 0.07), transparent)', filter: 'blur(70px)', animationDuration: '12s'}} />
        <div className="absolute top-[80%] right-[20%] w-[450px] h-[450px] rounded-full animate-pulse" style={{background: 'radial-gradient(circle, rgba(79, 70, 229, 0.08), transparent)', filter: 'blur(85px)', animationDuration: '9s'}} />
      </div>
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.225] z-0"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(255, 255, 255, 0.06) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.06) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          maskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)'
        }}
      />

      {/* Navbar (copied from Pricing/HomePage) */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <div className="mx-4 sm:mx-6 lg:mx-8 mt-4">
          <nav className="max-w-7xl mx-auto backdrop-blur-[6px] bg-[#09101c]/40 rounded-2xl border border-white/[0.1] font-sans">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                <div className="flex items-center">
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => navigate('/')}
                      className="flex items-center gap-2"
                    >
                      <img src={TiccAIlogo} alt="TiccAI" className="h-7 w-7" />
                      <span className="text-2xl font-bold text-white">TiccAI</span>
                    </button>
                  </div>
                </div>
                <div className="hidden md:block">
                  <div className="flex items-center space-x-8">
                    <button 
                      onClick={() => navigate('/#features')}
                      className="nav-button text-gray-300 font-sans"
                    >
                      <span>Features</span>
                    </button>
                    <button 
                      onClick={() => navigate('/#how-it-works')}
                      className="nav-button text-gray-300 font-sans"
                    >
                      <span>Dashboard</span>
                    </button>
                    <button 
                      onClick={() => navigate('/#integrations')}
                      className="nav-button text-gray-300 font-sans"
                    >
                      <span>Integrations</span>
                    </button>
                    <button 
                      onClick={() => openAuthModal('signin')}
                      className="nav-button text-gray-300 font-sans"
                    >
                      <span>Sign in</span>
                    </button>
                    <button 
                      onClick={() => openAuthModal('signup')}
                      className="get-started-button bg-gradient-to-r from-pink-500 to-pink-600 text-white px-6 py-2 rounded-full font-sans"
                    >
                      <span>Get started</span>
                    </button>
                  </div>
                </div>
                <div className="md:hidden">
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="text-gray-300 hover:text-white font-sans"
                  >
                    {isMobileMenuOpen ? <span>X</span> : <span>≡</span>}
                  </button>
                </div>
              </div>
              {isMobileMenuOpen && (
                <div className="md:hidden pb-4">
                  <div className="px-2 space-y-1">
                    <button 
                      onClick={() => { setIsMobileMenuOpen(false); navigate('/#features'); }}
                      className="nav-button block text-gray-300 py-2 font-sans"
                    >
                      <span>Features</span>
                    </button>
                    <button 
                      onClick={() => { setIsMobileMenuOpen(false); navigate('/#how-it-works'); }}
                      className="nav-button block w-full text-left text-gray-300 py-2 font-sans"
                    >
                      <span>Dashboard</span>
                    </button>
                    <button 
                      onClick={() => { setIsMobileMenuOpen(false); navigate('/#integrations'); }}
                      className="nav-button block w-full text-left text-gray-300 py-2 font-sans"
                    >
                      <span>Integrations</span>
                    </button>
                    <button 
                      onClick={() => { setIsMobileMenuOpen(false); openAuthModal('signin'); }}
                      className="nav-button block w-full text-left text-gray-300 py-2 font-sans"
                    >
                      <span>Sign in</span>
                    </button>
                    <button 
                      onClick={() => { setIsMobileMenuOpen(false); openAuthModal('signup'); }}
                      className="get-started-button w-full bg-gradient-to-r from-pink-500 to-pink-600 text-white px-6 py-2 rounded-full mt-4 font-sans"
                    >
                      <span>Get started</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>

      <main className="flex-1 relative z-10 flex items-center justify-center py-16 px-4 mt-12">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent animate-gradient">Get in Touch</h1>
            <p className="text-gray-400">
              Have questions or feedback? We'd love to hear from you.
            </p>
          </div>

          <div className="relative group rounded-2xl p-[2.5px] transition-all duration-500 hover:scale-[1.01] hover:shadow-2xl">
            <div className="absolute inset-0 rounded-2xl pointer-events-none z-0 transition-all duration-500 group-hover:opacity-100 opacity-90"
              style={{
                background: 'linear-gradient(120deg, rgba(236,72,153,0.35) 0%, rgba(168,85,247,0.25) 50%, rgba(59,130,246,0.18) 100%)',
                boxShadow: '0 0 32px 6px rgba(236,72,153,0.10), 0 0 0 2.5px rgba(236,72,153,0.18), 0 0 0 6px rgba(168,85,247,0.07)'
              }}
            />
            <div className="relative bg-gradient-to-r from-[#0A1420]/80 to-[#0D1824]/80 backdrop-blur-sm rounded-2xl border-2 border-transparent p-8 z-10">
              {status === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="h-8 w-8 text-green-500" />
                  </div>
                  <h2 className="text-xl font-semibold text-white mb-2">Message Sent!</h2>
                  <p className="text-gray-400 mb-2">
                    Thanks for reaching out. We'll get back to you soon.
                  </p>
                  <p className="text-white font-bold mb-6">We care about you.</p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="text-pink-500 hover:text-pink-400 transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.1] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      placeholder="you@example.com"
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-1">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.1] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      placeholder="What's this about?"
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.1] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                      placeholder="Tell us what's on your mind..."
                      required
                      disabled={isLoading}
                    />
                  </div>

                  {status === 'error' && (
                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
                      {errorMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-pink-500 to-pink-600 text-white py-3 rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <AuthModal isOpen={!!isAuthModalOpen} onClose={closeAuthModal} mode={isAuthModalOpen === 'signup' ? 'signup' : 'signin'} />
    </div>
  );
};

// Animated particles background component
function ParticlesBackground() {
  const [particles, setParticles] = useState(() =>
    Array.from({ length: 24 }, () => ({
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 8 + 4,
      color: Math.random() > 0.5 ? '147, 197, 253' : '236, 72, 153',
      duration: Math.random() * 12 + 16,
      delay: Math.random() * 10,
    }))
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {particles.map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full opacity-[0.32] animate-float"
          style={{
            width: `${p.size}px`,
            height: `${p.size}px`,
            left: `${p.left}%`,
            top: `${p.top}%`,
            background: `radial-gradient(circle at center, rgba(${p.color}, 0.8) 0%, rgba(${p.color}, 0.2) 50%, rgba(${p.color}, 0) 100%)`,
            boxShadow: `0 0 ${p.size * 2}px ${p.size / 2}px rgba(${p.color}, 0.3)`,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            filter: 'blur(0.5px)',
            willChange: 'transform',
          }}
        />
      ))}
    </div>
  );
}

export default Contact;